import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Stack,
  Alert,
  Snackbar,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
} from '@mui/icons-material';
import FacebookAdsService, {
  FacebookConnection,
} from '../../services/facebookAdsService';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

const FacebookAdsConnections: React.FC = () => {
  const [connections, setConnections] = useState<FacebookConnection[]>([]);
  const [loading, setLoading] = useState(true);
  const [connectionDialogOpen, setConnectionDialogOpen] = useState(false);
  const [connectionForm, setConnectionForm] = useState({
    accountId: '',
    accessToken: '',
    appId: '',
    permissions: ['ads_read', 'ads_management', 'read_insights'],
  });
  const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>({
    open: false,
    message: '',
    severity: 'success',
  });

  useEffect(() => {
    loadConnections();
  }, []);

  const loadConnections = async () => {
    setLoading(true);
    try {
      const response = await FacebookAdsService.listConnections();
      if (response.success && response.data) {
        setConnections(response.data);
      }
    } catch (error) {
      console.error('Erro ao carregar conexões:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateConnection = async () => {
    if (!connectionForm.accountId || !connectionForm.accessToken) {
      setSnackbar({
        open: true,
        message: 'Preencha Account ID e Access Token',
        severity: 'error',
      });
      return;
    }

    try {
      const response = await FacebookAdsService.createConnection(connectionForm);
      if (response.success) {
        setSnackbar({
          open: true,
          message: 'Conexão criada com sucesso',
          severity: 'success',
        });
        setConnectionDialogOpen(false);
        setConnectionForm({ accountId: '', accessToken: '', appId: '', permissions: ['ads_read', 'ads_management', 'read_insights'] });
        loadConnections();
      } else {
        setSnackbar({
          open: true,
          message: response.error || 'Erro ao criar conexão',
          severity: 'error',
        });
      }
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: error.message || 'Erro ao criar conexão',
        severity: 'error',
      });
    }
  };

  const handleDeleteConnection = async (accountId: number) => {
    if (!window.confirm('Tem certeza que deseja remover esta conexão?')) {
      return;
    }

    try {
      const response = await FacebookAdsService.deleteConnection(accountId.toString());
      if (response.success) {
        setSnackbar({
          open: true,
          message: 'Conexão removida com sucesso',
          severity: 'success',
        });
        loadConnections();
      } else {
        setSnackbar({
          open: true,
          message: response.error || 'Erro ao remover conexão',
          severity: 'error',
        });
      }
    } catch (error: any) {
      setSnackbar({
        open: true,
        message: error.message || 'Erro ao remover conexão',
        severity: 'error',
      });
    }
  };

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h5" sx={{ fontWeight: 'bold' }}>
          Conexões Facebook Ads
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setConnectionDialogOpen(true)}
        >
          Nova Conexão
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Conta</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Última Sincronização</TableCell>
              <TableCell>Permissões</TableCell>
              <TableCell align="center">Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {connections.map((conn) => (
              <TableRow key={conn.account_id}>
                <TableCell>
                  <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                    {conn.account_name || `Conta ${conn.account_id}`}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    ID: {conn.account_id}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    label={conn.is_active ? 'Ativa' : 'Inativa'}
                    color={conn.is_active ? 'success' : 'default'}
                    size="small"
                  />
                </TableCell>
                <TableCell>
                  {conn.last_sync_at
                    ? format(new Date(conn.last_sync_at), 'dd/MM/yyyy HH:mm', { locale: ptBR })
                    : 'Nunca'}
                </TableCell>
                <TableCell>
                  <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                    {conn.permissions.map((perm) => (
                      <Chip key={perm} label={perm} size="small" variant="outlined" />
                    ))}
                  </Box>
                </TableCell>
                <TableCell align="center">
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => handleDeleteConnection(conn.account_id)}
                  >
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
            {connections.length === 0 && (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  <Typography color="text.secondary">
                    Nenhuma conexão configurada. Clique em "Nova Conexão" para começar.
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Dialog: Nova Conexão */}
      <Dialog open={connectionDialogOpen} onClose={() => setConnectionDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Nova Conexão Facebook Ads</DialogTitle>
        <DialogContent>
          <Stack spacing={3} sx={{ mt: 1 }}>
            <TextField
              label="Account ID"
              value={connectionForm.accountId}
              onChange={(e) => setConnectionForm({ ...connectionForm, accountId: e.target.value })}
              fullWidth
              required
              helperText="ID numérico da sua conta de anúncios do Facebook"
            />
            <TextField
              label="Access Token"
              value={connectionForm.accessToken}
              onChange={(e) => setConnectionForm({ ...connectionForm, accessToken: e.target.value })}
              fullWidth
              required
              type="password"
              helperText="Token de acesso de longa duração (System User Token recomendado)"
            />
            <TextField
              label="App ID (Opcional)"
              value={connectionForm.appId}
              onChange={(e) => setConnectionForm({ ...connectionForm, appId: e.target.value })}
              fullWidth
              helperText="ID do aplicativo Meta for Developers"
            />
            <Alert severity="info">
              Para obter um token de acesso, consulte a documentação em Meta for Developers.
              Recomenda-se usar um Token de Usuário de Sistema para maior estabilidade.
            </Alert>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConnectionDialogOpen(false)}>Cancelar</Button>
          <Button onClick={handleCreateConnection} variant="contained">
            Criar Conexão
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default FacebookAdsConnections;


